import {
	fetchToolkitsV1,
	fetchToolkitV1,
	fetchToolkitTaxonomyV1,
	fetchDraftToolkitsV1,
	fetchDraftToolkitV1,
	fetchToolkitSearchV1
} from '../api/toolkits'
import {
	extractParentToolkitNids,
	getMostRecentlyUpdatedToolkit
} from '../../utils'

const defaultSortOptions = {
	updated: false,
	added: false
}

export const createToolkitsStore = (set) => ({
	toolkitCategories: null,
	toolkitCategoriesError: false,
	toolkitCategoriesErrorMessage: '',
	toolkit: null,
	toolkitError: false,
	sortOptions: { ...defaultSortOptions },
	filterOptions: {},
	roleOptions: {},
	defaultFilterOptions: {},
	defaultRoleOptions: {},
	orderedFilters: [],
	orderedRoles: [],
	fromSingleToolkitToToolkitsPage: false,
	toolkitSearchResponse: [],
	toolkitSearchError: false,
	mostRecentlyUpdatedToolkit: {
		nid: '',
		title: ''
	},
	setFromSingleToolkitToToolkitsPage: async (boolean) => {
		set({ fromSingleToolkitToToolkitsPage: boolean }, false, {
			type: 'setFromSingleToolkitToToolkitsPage',
			fromSingleToolkitToToolkitsPage: boolean
		})
	},
	fetchToolkitTaxonomy: async () => {
		try {
			const taxonomyData = await fetchToolkitTaxonomyV1()
			const roles = taxonomyData.toolkit_roles || {}
			const orderedRoles = Object.keys(roles)
				.map((key) => ({
					key: key.toLowerCase().replace(/\s+/g, ''),
					title: roles[key].title,
					id: roles[key].id,
					order: roles[key].order
				}))
				.sort((a, b) => a.order - b.order)

			let roleOptions = orderedRoles.reduce((acc, role) => {
				acc[role.key] = true
				return acc
			}, {})

			const params = new URLSearchParams(window.location.search)
			const queryRole = params.get('role')

			if (queryRole) {
				roleOptions = orderedRoles.reduce((acc, role) => {
					acc[role.key] = role.key === queryRole
					return acc
				}, {})
			}

			params.delete('role')
			const newSearch = params.toString()
			const newUrl = newSearch
				? `${window.location.pathname}?${newSearch}`
				: window.location.pathname

			window.history.replaceState({}, '', newUrl)

			const filters = taxonomyData.toolkit_types || {}
			const orderedFilters = Object.keys(filters)
				.map((key) => ({
					key: key.toLowerCase(),
					title: filters[key].title,
					id: filters[key].id,
					order: filters[key].order
				}))
				.sort((a, b) => a.order - b.order)

			const filterOptions = orderedFilters.reduce((acc, filter) => {
				acc[filter.key] = false
				return acc
			}, {})
			set(
				{
					roleOptions,
					filterOptions,
					defaultRoleOptions: roleOptions,
					defaultFilterOptions: filterOptions,
					orderedRoles,
					orderedFilters
				},
				false,
				{
					type: 'taxonomy/set',
					taxonomy: { roleOptions, filterOptions }
				}
			)
		} catch (error) {
			console.error('Error fetching taxonomy data:', error)
		}
	},
	fetchToolkitCategories: async () => {
		try {
			set({ toolkitCategoriesError: false }, false, {
				type: 'toolkitCategories/fetchError - Reset',
				error: false
			})
			const data = await fetchToolkitsV1()
			set({ toolkitCategories: data }, false, {
				type: 'toolkitCategories/fetch',
				toolkitCategories: data
			})
		} catch (error) {
			set(
				{ toolkitCategoriesErrorMessage: error.response.data.message },
				false,
				{ type: 'toolkitCategories/fetchErrorMessage', error: true }
			)
			set({ toolkitCategoriesError: true }, false, {
				type: 'toolkitCategories/fetchError',
				error: true
			})
		}
	},
	fetchToolkit: async (nnid) => {
		try {
			set({ toolkitError: false }, false, {
				type: 'toolkit/fetchError - reset',
				error: false
			})
			const toolkit = await fetchToolkitV1(nnid)
			set({ toolkit }, false, {
				type: 'toolkits/fetch',
				toolkit
			})
		} catch (error) {
			set({ toolkitError: true }, false, {
				type: 'toolkit/fetchError',
				error: true
			})
		}
	},
	clearToolkit: () => set({ toolkit: null }),
	clearToolkitCategories: () => set({ toolkitCategories: null }),
	setFilterOptions: (newFilterOptions) =>
		set({ filterOptions: newFilterOptions }, false, {
			type: 'filters/set',
			filterOptions: newFilterOptions
		}),
	setSortOptions: (newSortOptions) =>
		set({ sortOptions: newSortOptions }, false, {
			type: 'sort/set',
			sortOptions: newSortOptions
		}),
	setRoleOptions: (newRoleOptions) =>
		set({ roleOptions: newRoleOptions }, false, {
			type: 'roles/set',
			roleOptions: newRoleOptions
		}),
	resetFiltersSorts: () =>
		set(
			(state) => ({
				filterOptions: { ...state.defaultFilterOptions },
				roleOptions: { ...state.defaultRoleOptions },
				sortOptions: { ...defaultSortOptions }
			}),
			false,
			{
				type: 'filters/reset'
			}
		),
	fetchDraftToolkitCategories: async () => {
		try {
			set({ toolkitCategoriesError: false }, false, {
				type: 'toolkitCategories/fetchError - Reset',
				error: false
			})
			const data = await fetchDraftToolkitsV1()
			set({ toolkitCategories: data }, false, {
				type: 'toolkitCategories/fetch',
				toolkitCategories: data
			})
		} catch (error) {
			set(
				{ toolkitCategoriesErrorMessage: error.response.data.message },
				false,
				{ type: 'toolkitCategories/fetchErrorMessage', error: true }
			)
			set({ toolkitCategoriesError: true }, false, {
				type: 'toolkitCategories/fetchError',
				error: true
			})
		}
	},
	fetchDraftToolkit: async (nnid) => {
		try {
			set({ toolkitError: false }, false, {
				type: 'toolkit/fetchError - reset',
				error: false
			})
			const toolkit = await fetchDraftToolkitV1(nnid)
			set({ toolkit }, false, {
				type: 'toolkits/fetch',
				toolkit
			})
		} catch (error) {
			set({ toolkitError: true }, false, {
				type: 'toolkit/fetchError',
				error: true
			})
		}
	},
	fetchToolkitSearch: async (searchTerm, roleIds, typeIds) => {
		try {
			set({ toolkitSearchError: false }, false, {
				type: 'toolkitSearchError/fetchError - reset',
				error: false
			})
			const toolkitSearchResponse = await fetchToolkitSearchV1(
				searchTerm,
				roleIds,
				typeIds
			)
			set(
				{
					toolkitSearchResponse: extractParentToolkitNids(
						toolkitSearchResponse.results
					)
				},
				false,
				{
					type: 'toolkitSearch/fetch',
					toolkitSearchResponse: extractParentToolkitNids(
						toolkitSearchResponse.results
					)
				}
			)
		} catch (error) {
			set({ toolkitSearchError: true }, false, {
				type: 'toolkitSearchError/fetchError',
				error: true
			})
		}
	},
	clearToolkitSearchResponse: () => set({ toolkitSearchResponse: [] }),
	setMostRecentlyUpdatedToolkit: (data) => {
		const toolkit = getMostRecentlyUpdatedToolkit(data)
		set({ mostRecentlyUpdatedToolkit: toolkit }, false, {
			type: 'setMostRecentlyUpdatedToolkit',
			mostRecentlyUpdatedToolkit: toolkit
		})
	},
	clearMostRecentlyUpdatedToolkit: () =>
		set({
			mostRecentlyUpdatedToolkit: {
				nid: '',
				title: ''
			}
		})
})
